import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useRef, useState } from "react"

import ActionMenu from "src/components/ActionMenu"
import Tooltip from "src/components/Tooltip"

import ActionButton from "./ActionButton"
import {
  getSingleRowActions,
  getVisibleActions,
  isActionDisabled,
  sortActionsLinksLast,
  sortViewFirst,
  toMenuItems,
} from "./actionUtils"
import { ActionPropType } from "./types"

const MAX_VISIBLE_ACTIONS = 5
const MAX_SCALE = 1.15
const EFFECT_RADIUS = 80

const RowActions = ({
  actions,
  row,
  isVisible,
  onRequestConfirm,
  onClearHover,
  onMenuOpenChange,
}) => {
  const containerRef = useRef(null)
  const [hoveredRole, setHoveredRole] = useState(null)

  const updateScales = useCallback((cursorX) => {
    const container = containerRef.current
    if (!container) return

    const items = container.querySelectorAll("[data-dock-item]")
    items.forEach((item) => {
      const rect = item.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const distance = Math.abs(cursorX - centerX)

      let scale = 1
      if (distance < EFFECT_RADIUS) {
        scale =
          1 +
          (MAX_SCALE - 1) *
            ((Math.cos((Math.PI * distance) / EFFECT_RADIUS) + 1) / 2)
      }

      item.style.transform = `scale(${scale})`
    })
  }, [])

  const resetScales = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const items = container.querySelectorAll("[data-dock-item]")
    items.forEach((item) => {
      item.style.transform = "scale(1)"
    })
  }, [])

  const handleMouseMove = useCallback(
    (e) => updateScales(e.clientX),
    [updateScales]
  )

  useEffect(() => {
    if (!isVisible) return
    const container = containerRef.current
    if (!container) return
    const viewItem = container.querySelector('[data-role="view"]')
    if (!viewItem?.animate) return

    viewItem.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.15)" },
        { transform: "scale(1)" },
      ],
      { duration: 300, easing: "ease-out" }
    )
  }, [isVisible])

  const singleRowActions = getSingleRowActions(actions)
  const visibleActions = getVisibleActions(singleRowActions, row)
  const sortedActions = sortActionsLinksLast(sortViewFirst(visibleActions))

  const displayedActions = sortedActions.slice(0, MAX_VISIBLE_ACTIONS)
  const overflowActions = sortedActions.slice(MAX_VISIBLE_ACTIONS)

  const handleClick = (action, e) => {
    e.stopPropagation()
    if (action.confirm && onRequestConfirm) {
      onRequestConfirm(action, row)
    } else if (action.onClickSingle) {
      action.onClickSingle(row)
    }
  }

  const renderActionButton = (action) => {
    const disabled = isActionDisabled(action, row)
    const label = action.label

    return (
      <div
        key={label}
        data-dock-item
        data-role={action.role}
        onMouseEnter={() => setHoveredRole(action.role)}
        onMouseLeave={() => setHoveredRole(null)}
        className={classNames(
          "rounded-full p-1",
          action.danger
            ? "hover:bg-red-50"
            : action.role === "view"
            ? classNames("hover:bg-blue-50", {
                "bg-blue-50":
                  isVisible && (hoveredRole === null || hoveredRole === "view"),
              })
            : "hover:bg-gray-100"
        )}
        style={{
          transition:
            "transform 150ms ease-out, background-color 150ms ease-out",
        }}
      >
        <Tooltip text={label} variant="dark" tabIndex={-1}>
          <ActionButton
            icon={action.icon}
            iconStyle={action.iconStyle}
            label={label}
            href={action.href?.(row)}
            disabled={disabled}
            danger={action.danger}
            onClick={(e) => handleClick(action, e)}
          />
        </Tooltip>
      </div>
    )
  }

  if (visibleActions.length === 0) {
    return null
  }

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={resetScales}
      className={classNames(
        "flex items-center gap-2 overflow-visible px-3 py-2",
        isVisible
          ? "rounded border bg-white bg-gradient-to-tr shadow-md"
          : "pointer-events-none hidden"
      )}
    >
      {displayedActions.map(renderActionButton)}
      {overflowActions.length > 0 && (
        <div>
          <ActionMenu
            key={isVisible ? "visible" : "hidden"}
            items={toMenuItems(overflowActions, row, onRequestConfirm)}
            onOpenChange={(open) => {
              onMenuOpenChange?.(open)
              if (!open) {
                onClearHover?.()
              }
            }}
          />
        </div>
      )}
    </div>
  )
}

RowActions.propTypes = {
  actions: PropTypes.arrayOf(ActionPropType).isRequired,
  row: PropTypes.object.isRequired,
  isVisible: PropTypes.bool,
  onRequestConfirm: PropTypes.func,
  onClearHover: PropTypes.func,
  onMenuOpenChange: PropTypes.func,
}

export default RowActions
